/**
 * `/oracle/:map` — watch the oracle play a kitchen, with its numbers beside it.
 *
 * Nothing here is new machinery. The oracle's round is stored as an ordinary
 * version-6 session log, so the page is the replay chain the `/replay` page
 * already is: `parseLog` → `createPlayer` → an `EventFeed({ live: false })` →
 * the same `Stage` a live round draws on. What it adds is where the log comes
 * from (`/api/oracle/:map/log`) and the metrics panel.
 *
 * The server never solves on a request. A kitchen it has not solved yet
 * answers `202`, and the page says "solving…" and asks again every few
 * seconds until the background loop gets there. The same goes for the
 * baseline beside it, which is solved after the Oracle: its row says it is
 * working, and the page asks again for the summary until it is stored.
 *
 * Beside the kitchen picker, "Watch: Oracle | Baseline" swaps the round on the
 * stage for the baseline's (`/log?plan=baseline`) and back, from 0:00. The
 * panel keeps the Oracle's numbers either way: the baseline is one row of it,
 * not a second panel. The line under the stage says whose round is playing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ClientConfig,
  OracleListEntry,
  OraclePlan,
  OracleStatus,
  OracleSummary,
  SessionLog,
  SessionLogHeader,
} from '@overcooked/shared'
import { TICK_MS } from '@overcooked/shared'
import { EventFeed } from '../feed/feed'
import { prettyMapName } from '../flow/display'
import { buildCatalog } from '../render/catalog'
import { Stage } from '../render/Stage'
import type { ReplayPlayer } from '../replay/file'
import { createPlayer, parseLog } from '../replay/file'
import { MetricsPanel } from './MetricsPanel'
import type { OracleRoute } from './path'
import { logPathFor, oraclePathFor } from './path'
import { points, statusLabel } from './summary'

/** How long a page waits before asking again about a kitchen still being solved. */
const POLL_MS = 3_000

const clock = (ticks: number): string => {
  const seconds = Math.max(0, Math.round((ticks * TICK_MS) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** Whose round is on the stage, in the words the line under it uses. */
const PLAN_NAME: Readonly<Record<OraclePlan, string>> = { oracle: 'Oracle', baseline: 'Baseline' }

/**
 * `Galley · 230 points`, `Galley · 230 points (partial)` when the safety abort
 * cut the solve short (the landing page's word), `Medium · Working…`: one
 * option of the kitchen picker.
 */
const optionLabel = (row: OracleListEntry): string =>
  `${prettyMapName(row.map)} · ${
    row.status === 'ready' && row.score !== undefined
      ? `${points(row.score)}${row.truncated ? ' (partial)' : ''}`
      : statusLabel(row.status)
  }`

export function OraclePage({ route }: { route: OracleRoute }) {
  // `live: false`: the transport owns the playhead, exactly as on `/replay`.
  const feed = useMemo(() => new EventFeed(undefined, { live: false }), [])
  const [map, setMap] = useState<string | null>(route.kind === 'map' ? route.map : null)
  const [config, setConfig] = useState<ClientConfig | null>(null)
  // Whether the config request has finished, either way: `/oracle` alone waits
  // for it, so it opens the server's default kitchen rather than the first name.
  const [configSettled, setConfigSettled] = useState(false)
  const [list, setList] = useState<OracleListEntry[] | null>(null)
  const [summary, setSummary] = useState<OracleSummary | null>(null)
  const [status, setStatus] = useState<OracleStatus | null>(null)
  const [header, setHeader] = useState<SessionLogHeader | null>(null)
  const [message, setMessage] = useState(route.kind === 'malformed' ? '' : 'Loading…')
  const [progress, setProgress] = useState({ tick: 0, playing: false })
  const [lastTick, setLastTick] = useState(0)
  const playerRef = useRef<ReplayPlayer | null>(null)
  const [player, setPlayer] = useState<ReplayPlayer | null>(null)
  // Whose round is on the stage. Every kitchen opens on the Oracle's.
  const [plan, setPlan] = useState<OraclePlan>('oracle')
  // The speed a swapped-in round keeps playing at.
  const [speed, setSpeed] = useState(1)
  const speedRef = useRef(1)

  // The fallback catalogue, and which kitchen `/oracle` alone should open.
  useEffect(() => {
    let cancelled = false
    void fetch('/api/config')
      .then((response) => response.json() as Promise<ClientConfig>)
      .then((value) => {
        if (!cancelled) setConfig(value)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setConfigSettled(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Every kitchen and where its result stands, for the picker.
  useEffect(() => {
    let cancelled = false
    void fetch('/api/oracle')
      .then((response) => (response.ok ? (response.json() as Promise<OracleListEntry[]>) : []))
      .then((rows) => {
        if (!cancelled) setList(rows)
      })
      .catch(() => {
        if (!cancelled) setList([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // `/oracle` on its own opens the default kitchen, and says so in the address.
  useEffect(() => {
    if (route.kind !== 'index' || map !== null || list === null || !configSettled) return
    const names = list.map((row) => row.map)
    const fallback = config?.defaultMap && names.includes(config.defaultMap) ? config.defaultMap : (names[0] ?? null)
    if (fallback) {
      window.history.replaceState(null, '', oraclePathFor(fallback))
      setMap(fallback)
    } else {
      setMessage('No kitchens on this server.')
    }
  }, [route.kind, map, list, config, configSettled])

  useEffect(() => () => playerRef.current?.stop(), [])

  // Another kitchen opens on the Oracle's round.
  useEffect(() => setPlan('oracle'), [map])

  const mount = useCallback(
    (log: SessionLog, whose: OraclePlan) => {
      playerRef.current?.stop()
      setHeader(log.header)
      const next = createPlayer(log.frames, feed, {
        onProgress: (tick, playing) => setProgress({ tick, playing }),
      })
      playerRef.current = next
      setPlayer(next)
      setLastTick(next.index.lastTick)
      next.setSpeed(speedRef.current)
      next.seek(next.index.firstTick)
      next.play()
      setMessage(`${PLAN_NAME[whose]} round on ${prettyMapName(log.header?.mapName ?? '')} · ${clock(next.index.lastTick)}`)
    },
    [feed],
  )

  // The summary; or "solving…" and another look in a moment.
  useEffect(() => {
    if (!map) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const again = (): void => {
      timer = setTimeout(() => void load(), POLL_MS)
    }
    const load = async (): Promise<void> => {
      try {
        const response = await fetch(`/api/oracle/${encodeURIComponent(map)}`)
        if (cancelled) return
        if (response.status === 202) {
          setStatus('solving')
          setMessage('Still working on this kitchen. Checking again…')
          again()
          return
        }
        if (response.status === 404 || response.status === 400) {
          setStatus(null)
          setMessage(`No kitchen called "${map}" here.`)
          return
        }
        if (!response.ok) {
          setMessage(`Server error (${response.status}). Trying again…`)
          again()
          return
        }
        const value = (await response.json()) as OracleSummary
        if (cancelled) return
        setSummary(value)
        setStatus(value.status)
        if (value.status !== 'ready') {
          setMessage(
            value.status === 'failed'
              ? "The oracle couldn't solve this kitchen."
              : 'No dish can be served in this kitchen.',
          )
          return
        }
      } catch {
        if (cancelled) return
        setMessage("Couldn't reach the server. Trying again…")
        again()
      }
    }
    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [map])

  // Once the kitchen is solved, the round of whichever plan is chosen: the
  // Oracle's on arrival, the baseline's after a tap on Baseline. Only the plan
  // or the kitchen changing loads a round, so a summary asked for again (the
  // baseline's row resolving) leaves the round on screen alone.
  const ready = summary?.status === 'ready'
  useEffect(() => {
    if (!map || !ready) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const again = (): void => {
      timer = setTimeout(() => void load(), POLL_MS)
    }
    const load = async (): Promise<void> => {
      try {
        const response = await fetch(logPathFor(map, plan))
        if (cancelled) return
        if (response.status === 202) {
          again()
          return
        }
        if (!response.ok) {
          const whose = plan === 'baseline' ? "the baseline's" : "the oracle's"
          setMessage(`Couldn't load ${whose} round (${response.status}).`)
          return
        }
        const log = parseLog(await response.text())
        if (!cancelled) mount(log, plan)
      } catch {
        if (cancelled) return
        setMessage("Couldn't reach the server. Trying again…")
        again()
      }
    }
    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [map, plan, ready, mount])

  /** Swap the round on the stage; the one playing pauses while the other loads. */
  const watch = (next: OraclePlan): void => {
    if (next === plan) return
    playerRef.current?.pause()
    setMessage('Loading…')
    setPlan(next)
  }
  const baselineReady = summary?.baseline?.status === 'ready'

  // The baseline is solved after every Oracle, so on a fresh server the
  // Oracle's round can be on screen while the baseline's row still says it
  // is working. Ask again for the summary alone until it is stored; the round
  // on screen is left playing.
  const baselinePending = summary?.baseline?.status === 'solving'
  useEffect(() => {
    if (!map || !baselinePending) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const again = (): void => {
      timer = setTimeout(() => void look(), POLL_MS)
    }
    const look = async (): Promise<void> => {
      try {
        const response = await fetch(`/api/oracle/${encodeURIComponent(map)}`)
        if (cancelled) return
        if (response.status === 200) {
          const value = (await response.json()) as OracleSummary
          if (cancelled) return
          setSummary(value)
          if (value.baseline?.status === 'solving') again()
          return
        }
      } catch {
        // A blip: try again in a moment, like the first load does.
      }
      if (!cancelled) again()
    }
    again()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [map, baselinePending])

  const catalog = useMemo(() => buildCatalog(header?.catalogue ?? config), [header, config])
  const dishLabel = useCallback((id: string) => catalog.dish(id)?.label ?? id, [catalog])

  // The picker lists every kitchen the server has, including the one open even
  // before the list has loaded.
  const options = useMemo(() => {
    const rows = list ?? []
    if (map && !rows.some((row) => row.map === map)) {
      return [{ map, status: status ?? 'solving', budgetLabel: '' } as OracleListEntry, ...rows]
    }
    return rows
  }, [list, map, status])

  return (
    <main className="page oracle-page">
      <header className="bar">
        <span className="brand">
          Over<em>cooked</em>
        </span>
        <span className="code">Oracle</span>
        <label className="oracle-pick">
          <span>Kitchen</span>
          <select
            id="oracle-map"
            value={map ?? ''}
            disabled={options.length === 0}
            onChange={(event) => window.location.assign(oraclePathFor(event.target.value))}
          >
            {map === null && <option value="">Choose a kitchen…</option>}
            {options.map((row) => (
              <option key={row.map} value={row.map}>
                {optionLabel(row)}
              </option>
            ))}
          </select>
        </label>
        {route.kind !== 'malformed' && (
          <div className="oracle-plan" role="group" aria-labelledby="oracle-plan-label">
            <span id="oracle-plan-label">Watch</span>
            <button
              type="button"
              data-testid="oracle-plan-oracle"
              aria-pressed={plan === 'oracle'}
              onClick={() => watch('oracle')}
            >
              Oracle
            </button>
            <button
              type="button"
              data-testid="oracle-plan-baseline"
              aria-pressed={plan === 'baseline'}
              disabled={!baselineReady && plan !== 'baseline'}
              onClick={() => watch('baseline')}
            >
              Baseline
            </button>
          </div>
        )}
        <a className="bar-link" href="/">
          Play
        </a>
      </header>

      {route.kind === 'malformed' ? (
        <p className="error">
          "{route.text}" isn't a kitchen. Pick one above, or <a href="/oracle">open the default</a>.
        </p>
      ) : (
        <div className="oracle-layout">
          <section className="replay-stage oracle-stage">
            <Stage feed={feed} catalog={catalog} selfId={null} className="stage-replay" />
            <div className="replay-transport oracle-transport">
              <button
                type="button"
                data-testid="oracle-play"
                disabled={!player}
                onClick={() => (progress.playing ? player?.pause() : player?.play())}
              >
                {progress.playing ? 'Pause' : 'Play'}
              </button>
              <input
                type="range"
                aria-label="Seek"
                data-testid="oracle-seek"
                min={0}
                max={Math.max(1, lastTick)}
                value={Math.round(progress.tick)}
                disabled={!player}
                onChange={(event) => player?.seek(Number(event.target.value))}
              />
              <span className="mono" data-testid="oracle-time" data-tick={Math.round(progress.tick)}>
                {clock(progress.tick)} / {clock(lastTick)}
              </span>
              <select
                aria-label="Speed"
                data-testid="oracle-speed"
                value={String(speed)}
                disabled={!player}
                onChange={(event) => {
                  const next = Number(event.target.value)
                  speedRef.current = next
                  setSpeed(next)
                  player?.setSpeed(next)
                }}
              >
                <option value="0.5">0.5x</option>
                <option value="1">1x</option>
                <option value="2">2x</option>
                <option value="4">4x</option>
              </select>
            </div>
            <p className="hint" data-testid="oracle-message">
              {message}
            </p>
          </section>
          <MetricsPanel
            summary={summary}
            status={status}
            headerContentHash={header?.contentHash ?? null}
            dishLabel={dishLabel}
          />
        </div>
      )}
    </main>
  )
}

export default OraclePage
